import { RotateLeftOutlined, RotateRightOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { UploadSingleFile } from '@/constants';
import { storageAuth } from '@/utils';
import 'cropperjs/dist/cropper.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-cropper';

export interface ImageCropModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 完成后返回已上传图片 URL */
  onDone: (url: string) => void;
  /** 目标图宽高比，默认 2:1（轮播常用） */
  aspect?: number;
  /** 裁剪区域高度 */
  cropHeight?: number;
  /** 弹窗宽度 */
  modalWidth?: number;
  /** cropper viewMode，0 裁切框不受画布限制 */
  viewMode?: 0 | 1 | 2 | 3;
  /** 初始图片数据 */
  initialImage?: string;
}

/**
 * 通用图片裁剪上传弹窗
 * - 先选择本地图片 → 裁剪 → 导出 Blob → 上传至统一上传接口 → 回传 URL
 */
const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  aspect = 1,
  cropHeight = 400,
  modalWidth = 800,
  viewMode = 1,
  onOpenChange,
  onDone,
  initialImage,
}) => {
  const [image, setImage] = useState<string | undefined>();
  const uploadingRef = useRef(false);
  const cropperRef = useRef<any>(null);
  // 当Modal打开时设置初始图片
  useEffect(() => {
    if (open && initialImage) {
      setImage(initialImage);
    }
  }, [open, initialImage]);

  const tokenHeaders = useMemo<Record<string, string>>(() => {
    const parsedValue = storageAuth.get();
    if (!parsedValue) return {};
    return {
      [parsedValue.tokenName]: parsedValue.tokenValue,
    };
  }, []);

  const onSelectFile = (file: RcFile) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => setImage(reader.result as string));
    reader.readAsDataURL(file);
    return false;
  };

  const getCroppedImageBlob = async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) {
        reject('Cropper not initialized');
        return;
      }

      const canvas = cropper.getCroppedCanvas();
      if (!canvas) {
        reject('Failed to get cropped canvas');
        return;
      }

      canvas.toBlob(
        (blob: Blob | null) => {
          if (!blob) {
            reject('Failed to create blob');
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.9,
      );
    });
  };

  const doUpload = async (blob: Blob): Promise<string> => {
    const formData = new FormData();
    formData.append('file', blob, 'crop.jpg');
    const res = await fetch(UploadSingleFile, {
      method: 'POST',
      headers: {
        ...tokenHeaders,
      } as any,
      body: formData,
    });
    const data = await res.json();
    if (data?.code === 0) {
      return typeof data.data === 'string' ? data.data : data.data?.url;
    }
    throw new Error(data?.msg || '上传失败');
  };

  const handleOk = async () => {
    const cropper = cropperRef.current?.cropper;
    if (!image || !cropper || uploadingRef.current) return;
    uploadingRef.current = true;
    try {
      const blob = await getCroppedImageBlob();
      const url = await doUpload(blob);
      onDone(url);
      onOpenChange(false);
    } catch (e) {
      console.error(e);
    } finally {
      uploadingRef.current = false;
    }
  };

  const resetState = () => {
    setImage(undefined);
  };

  return (
    <Modal
      title="图片裁剪"
      open={open}
      onCancel={() => {
        onOpenChange(false);
        resetState();
      }}
      maskClosable={false}
      onOk={handleOk}
      okButtonProps={{ disabled: !image }}
      destroyOnHidden
      width={modalWidth}
    >
      {!image ? (
        <Upload
          beforeUpload={onSelectFile}
          showUploadList={false}
          accept="image/*"
        >
          <div
            style={{
              width: 200,
              height: 200,
              border: '1px dashed #d9d9d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            选择图片
          </div>
        </Upload>
      ) : (
        <div>
          <div style={{ width: '100%', height: cropHeight }}>
            <Cropper
              ref={cropperRef}
              src={image}
              style={{ height: cropHeight, width: '100%' }}
              zoomTo={0.1}
              crossOrigin="anonymous"
              aspectRatio={aspect}
              viewMode={viewMode}
              minCropBoxHeight={10}
              minCropBoxWidth={10}
              background={false}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
              guides={true}
            />
          </div>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Space>
              <Upload
                beforeUpload={onSelectFile}
                showUploadList={false}
                accept="image/*"
              >
                <Button>重新上传</Button>
              </Upload>
              <Button
                icon={<RotateLeftOutlined />}
                onClick={() => {
                  const cropper = cropperRef.current?.cropper;
                  if (cropper) {
                    cropper.rotate(-90);
                  }
                }}
              >
                向左旋转
              </Button>
              <Button
                icon={<RotateRightOutlined />}
                onClick={() => {
                  const cropper = cropperRef.current?.cropper;
                  if (cropper) {
                    cropper.rotate(90);
                  }
                }}
              >
                向右旋转
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ImageCropModal;
